

import React, { useMemo } from 'react';
import { Logo } from './Logo';
import { formatCurrency, formatDate } from '../constants';
import { BriefcaseIcon } from './icons/BriefcaseIcon';
import { DocumentTextIcon } from './icons/DocumentTextIcon';
import { CalendarIcon } from './icons/CalendarIcon';
import { CurrencyDollarIcon } from './icons/CurrencyDollarIcon';
import { MapPinIcon } from './icons/MapPinIcon';

// Define props interface for the component
interface PrintableDashboardProps {
    stats: any;
    costByCategoryData: any[];
    costEvolutionData: any[];
    latestExpenses: any[];
    obrasPorRegiao: any[];
    currentMonthDataIndex: number;
    evolutionFilterLabel: string;
}

// Print-friendly Bar Chart Component
const PrintBarChart: React.FC<{ data: any[] }> = ({ data }) => {
    const maxValue = Math.max(...data.map(d => d.value), 1);
    return (
        <div className="h-48 flex items-end space-x-2 p-2 border border-gray-200 rounded">
            {data.map((item, index) => (
                <div key={index} className="flex-1 flex flex-col items-center justify-end h-full">
                    {item.percentage && item.percentage > 0.1 && (
                        <div className="text-[8px] font-bold text-black mb-1">
                            {`${item.percentage.toFixed(1)}%`}
                        </div>
                    )}
                    <div className="w-full rounded-t" style={{ height: `${(item.value / maxValue) * 75}%`, backgroundColor: item.color }} />
                    <div className="text-[8px] text-center text-black mt-1 w-full font-semibold leading-tight">
                        {item.label.split('\n').map((part: string, i: number) => <div key={i}>{part}</div>)}
                    </div>
                    <div className="text-[8px] text-center text-gray-600 w-full">{formatCurrency(item.value)}</div>
                </div>
            ))}
        </div>
    );
};

// Main PrintableDashboard component
export const PrintableDashboard: React.FC<PrintableDashboardProps> = ({
  stats,
  costByCategoryData,
  costEvolutionData,
  latestExpenses,
  obrasPorRegiao,
  evolutionFilterLabel,
}) => {
  const today = new Date();
  
  const evolutionChartDataForPrint = useMemo(() => {
    if (!costEvolutionData || !costEvolutionData[0] || !costEvolutionData[0].points) {
        return [];
    }
    const series = costEvolutionData[0];
    const total = series.points.reduce((sum: number, p: {value: number}) => sum + p.value, 0);

    return series.points.map((point: {label: string, value: number}) => ({
        label: point.label,
        value: point.value,
        color: series.color,
        percentage: total > 0 ? (point.value / total) * 100 : 0,
    }));
  }, [costEvolutionData]);

  return (
    <div className="bg-white p-6 font-sans text-black" style={{ width: '210mm' }}>
        {/* Page Header */}
        <header className="flex justify-between items-center pb-4 mb-4 border-b-2 border-gray-400">
            <div className="h-12"><Logo isSidebarOpen={true} /></div>
            <div className='text-right'>
                <h1 className="text-2xl font-bold text-black">Relatório do Dashboard</h1>
                <p className="text-sm text-gray-600">Gerado em: {today.toLocaleDateString('pt-BR')}</p>
            </div>
        </header>

        <main className="space-y-6">
            {/* Stats Section */}
            <section>
                <h2 className="text-lg font-bold border-b border-gray-300 pb-1 mb-2">Resumo Geral</h2>
                <div className="grid grid-cols-4 gap-4 text-center">
                    <div className="p-2 bg-gray-100 rounded">
                        <BriefcaseIcon className="text-2xl mx-auto text-brand-teal"/>
                        <p className="text-xs text-gray-700 mt-1">Total de Obras</p>
                        <p className="text-lg font-bold">{stats.totalObras}</p>
                    </div>
                    <div className="p-2 bg-gray-100 rounded">
                        <DocumentTextIcon className="text-2xl mx-auto text-brand-teal"/>
                        <p className="text-xs text-gray-700 mt-1">Custo ({stats.currentMonthName})</p>
                        <p className="text-lg font-bold">{formatCurrency(stats.custoMesAtual)}</p>
                    </div>
                    <div className="p-2 bg-gray-100 rounded">
                        <CalendarIcon className="text-2xl mx-auto text-brand-teal"/>
                        <p className="text-xs text-gray-700 mt-1">Lançamentos Hoje</p>
                        <p className="text-lg font-bold">{formatCurrency(stats.lancamentosHoje)}</p>
                    </div>
                    <div className="p-2 bg-gray-100 rounded">
                        <CurrencyDollarIcon className="text-2xl mx-auto text-brand-teal"/>
                        <p className="text-xs text-gray-700 mt-1">Custo Total Geral</p>
                        <p className="text-lg font-bold">{formatCurrency(stats.custoTotalGeral)}</p>
                    </div>
                </div>
            </section>
            
            {/* Chart Section */}
            <section>
                <h2 className="text-lg font-bold border-b border-gray-300 pb-1 mb-2">Custos por Categoria (Total)</h2>
                <PrintBarChart data={costByCategoryData} />
            </section>
            
            {/* Evolution Chart Section */}
            <section>
                <h2 className="text-lg font-bold border-b border-gray-300 pb-1 mb-2">Evolução de Custos ({evolutionFilterLabel})</h2>
                <PrintBarChart data={evolutionChartDataForPrint} />
            </section>

            {/* Lists Section */}
            <section className="grid grid-cols-2 gap-6">
                <div>
                    <h2 className="text-lg font-bold border-b border-gray-300 pb-1 mb-2">Últimos Lançamentos</h2>
                    <div className="space-y-2">
                        {latestExpenses.map((exp: any) => (
                            <div key={exp.id} className="flex justify-between items-center text-xs p-1 bg-gray-50 rounded">
                                <div>
                                    <p className="font-semibold">{exp.subCategoryItemName}</p>
                                    <p className="text-gray-600">{exp.projectName}</p>
                                </div>
                                <p className="font-bold text-brand-lime">{formatCurrency(exp.amount)}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <div>
                    <h2 className="text-lg font-bold border-b border-gray-300 pb-1 mb-2">Obras por Região</h2>
                    <div className="space-y-2">
                         {obrasPorRegiao.map((item: any) => (
                            <div key={item.state} className="flex justify-between items-center text-xs p-1 bg-gray-50 rounded">
                                <div className="flex items-center">
                                    <MapPinIcon className="text-base mr-2 text-brand-teal"/>
                                    <p className="font-semibold">{item.state} ({item.count} {item.count > 1 ? 'obras' : 'obra'})</p>
                                </div>
                                <div>
                                    <p className="font-bold text-brand-lime text-right">{formatCurrency(item.totalCost)}</p>
                                    <p className="text-gray-600 text-right">({item.percentage.toFixed(2)}%)</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

        </main>
    </div>
  );
};